import React, { useEffect, useState } from "react";
import { agentApi } from "../../services/agentApi";
import {
  SESSION_STATUS_COLOR,
  SESSION_STATUS_LABEL,
  sessionStatusLabel,
  sessionStepLabel,
} from "./sessionLabels";

const STATUS_OPTIONS = [
  { value: "", label: "Todas" },
  ...Object.entries(SESSION_STATUS_LABEL).map(([value, label]) => ({ value, label })),
];

function formatListStamp(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ConversationList({ selectedId, onSelect }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  async function load() {
    setLoading(true);
    setAuthError(false);
    try {
      const r = await agentApi.sessions.list({ status, search, limit: 50 });
      setItems(r.sessions || []);
    } catch (err) {
      if (err?.status === 401 || err?.message?.includes("401") || err?.message?.toLowerCase().includes("unauthorized")) {
        setAuthError(true);
      }
      console.error("Failed to load sessions", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [status, search]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-gray-200 space-y-2 bg-white">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm focus:outline-none focus:border-blue-500"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Buscar nome / telefone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm focus:outline-none focus:border-blue-500 placeholder-gray-400"
        />
        <button
          type="button"
          disabled={exporting}
          onClick={async () => {
            setExporting(true);
            setExportError("");
            try {
              await agentApi.sessions.exportExcel({ status, search });
            } catch (err) {
              setExportError("Não foi possível gerar o relatório.");
            } finally {
              setExporting(false);
            }
          }}
          className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-700 rounded-md hover:bg-blue-800 disabled:opacity-60"
        >
          {exporting ? "Gerando relatório..." : "Exportar Excel"}
        </button>
        {exportError ? (
          <p className="text-xs text-red-600">{exportError}</p>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-gray-500 text-sm">Carregando...</div>
        ) : authError ? (
          <div className="p-4 text-sm text-red-600 bg-red-50 border-b border-red-100">
            Sessão expirada — faça login novamente.
          </div>
        ) : items.length === 0 ? (
          <div className="p-4 text-gray-400 text-sm">Nenhuma conversa.</div>
        ) : (
          items.map((s) => (
            <button
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={`w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 transition ${
                selectedId === s.id
                  ? "bg-blue-50 border-l-4 border-l-blue-600"
                  : "border-l-4 border-l-transparent"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-gray-900 text-sm font-medium truncate">
                  {s.lead?.name || s.lead?.phone || "—"}
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${SESSION_STATUS_COLOR[s.status] || "bg-gray-100 text-gray-600"}`}
                >
                  {sessionStatusLabel(s.status)}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">{s.lead?.phone}</div>
              <div className="text-xs text-gray-400 mt-1 flex justify-between">
                <span>Etapa: {sessionStepLabel(s.currentStep)}</span>
                <span>{formatListStamp(s.updatedAt || s.lastInboundAt || s.createdAt)}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
